import {
  Box, Typography, Card, CardContent, CardActions, Button, Chip, Stack, Alert, Divider, CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import { itemsApi, type SubstituteOptionDto } from "@/api/endpoints";

interface SubstitutePanelProps {
  primaryItemId: string;
  primaryItemName: string;
  warehouseId: string;
  requiredQty: number;
  onSelect: (sub: SubstituteOptionDto) => void;
  onClose: () => void;
}

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

const REASON_LABEL: Record<string, string> = {
  OutOfStock: "Out of stock",
  CheaperEquivalent: "Cheaper equivalent",
  PatientPreference: "Patient preference",
  Therapeutic: "Therapeutic substitute",
  Generic: "Generic equivalent",
};

export function SubstitutePanel({
  primaryItemId, primaryItemName, warehouseId, requiredQty, onSelect, onClose,
}: SubstitutePanelProps) {
  const query = useQuery({
    queryKey: ["substitutes", primaryItemId, warehouseId],
    queryFn: () => itemsApi.substitutes(primaryItemId, warehouseId),
    enabled: !!primaryItemId && !!warehouseId,
  });

  return (
    <Box
      sx={{ p: 2, borderLeft: 1, borderColor: "divider", height: "100%", overflowY: "auto" }}
      data-testid="billing-substitute-panel"
      data-primary-item-id={primaryItemId}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SwapHorizIcon color="warning" />
          <Typography variant="h6">Substitute options</Typography>
        </Stack>
        <Button size="small" startIcon={<CloseIcon fontSize="small" />} onClick={onClose} data-testid="billing-substitute-close">Close</Button>
      </Stack>

      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
        <strong>{primaryItemName}</strong> needs {requiredQty} unit{requiredQty === 1 ? "" : "s"}.
        Choose a substitute below.
      </Alert>

      {query.isLoading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}
      {query.isError && <Alert severity="error">Could not load substitutes.</Alert>}
      {query.data?.length === 0 && (
        <Alert severity="info">No substitutes configured for this item.</Alert>
      )}

      <Stack spacing={1.5}>
        {query.data?.map((s) => {
          const hasStock = s.availableQty >= requiredQty;
          return (
            <Card
              key={s.itemId}
              variant="outlined"
              sx={{ borderColor: hasStock ? "success.light" : "warning.light" }}
              data-testid={`billing-substitute-card-${s.sku}`}
              data-substitute-item-id={s.itemId}
              data-substitute-available={s.availableQty}
            >
              <CardContent sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle2">{s.sku} — {s.name}</Typography>
                    {s.genericName && (
                      <Typography variant="caption" color="text.secondary">{s.genericName}</Typography>
                    )}
                  </Box>
                  <Chip size="small" label={`Priority ${s.priority}`} variant="outlined" />
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap" }}>
                  {s.form && <Chip size="small" label={s.form} variant="outlined" />}
                  {s.strength && <Chip size="small" label={s.strength} variant="outlined" />}
                  {s.manufacturer && <Chip size="small" label={s.manufacturer} variant="outlined" />}
                </Stack>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {REASON_LABEL[s.reasonCode] ?? s.reasonCode}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {s.mrp != null && <Typography variant="caption">MRP {inr.format(s.mrp)}</Typography>}
                    <Chip size="small"
                      color={hasStock ? "success" : "warning"}
                      label={hasStock ? `${s.availableQty} avail.` : `Only ${s.availableQty} avail.`} />
                  </Stack>
                </Stack>
                {s.requiresPharmacistConfirm && (
                  <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
                    Pharmacist confirmation required for this substitution.
                  </Alert>
                )}
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <Button
                  size="small" variant="contained" onClick={() => onSelect(s)}
                  disabled={s.availableQty === 0}
                  data-testid={`billing-substitute-use-${s.sku}`}
                >
                  Use this substitute
                </Button>
              </CardActions>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}
